import { useEffect, useState } from 'react';
import Button from '../components/Button';
import Input from '../components/Input';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { companyService } from '../services/adminApi';

interface Company {
  id: string;
  code: string;
  name: string;
  uen?: string;
  address?: string;
  logo_url?: string;
  payslip_logo_url?: string;
  active: boolean;
}

const EMPTY_FORM = { code: '', name: '', uen: '', address: '' };

export default function CompanyAdmin() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Company | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await companyService.list();
      setCompanies(res.data.companies || []);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (company: Company) => {
    setEditing(company);
    setFormData({ code: company.code, name: company.name, uen: company.uen || '', address: company.address || '' });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setConfirmOpen(true);
  };

  const performSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await companyService.update(editing.id, { name: formData.name, uen: formData.uen, address: formData.address });
      } else {
        await companyService.create(formData);
      }
      setConfirmOpen(false);
      setShowForm(false);
      load();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (company: Company, file: File, kind: 'mobile' | 'payslip') => {
    try {
      await companyService.uploadLogo(company.id, file, kind);
      load();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to upload logo');
    }
  };

  const columns: Column<Company>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name', sortable: true },
    { key: 'uen', header: 'UEN', render: (row) => row.uen || '—' },
    { key: 'address', header: 'Address' },
    {
      key: 'active',
      header: 'Status',
      render: (row) => (row.active ? 'Active' : 'Inactive'),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-3 items-center">
          <button onClick={() => openEdit(row)} className="text-blue-600 hover:underline text-sm">
            Edit
          </button>
          <label className="text-sm text-gray-600 hover:underline cursor-pointer">
            Upload Mobile Logo
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files && handleLogoUpload(row, e.target.files[0], 'mobile')}
            />
          </label>
          <label className="text-sm text-gray-600 hover:underline cursor-pointer">
            Upload Payslip Logo
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files && handleLogoUpload(row, e.target.files[0], 'payslip')}
            />
          </label>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Companies</h2>
        <Button onClick={openCreate}>Add Company</Button>
      </div>

      <Table columns={columns} rows={companies} loading={loading} emptyMessage="No companies yet" />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Company' : 'Add Company'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Company Code (max 6 chars)"
            maxLength={6}
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            disabled={!!editing}
            required
          />
          <Input
            label="Company Name (max 50 chars)"
            maxLength={50}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="UEN"
            maxLength={20}
            value={formData.uen}
            onChange={(e) => setFormData({ ...formData, uen: e.target.value.toUpperCase() })}
          />
          <Input
            label="Address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          />
          <Button type="submit" className="w-full">
            {editing ? 'Save Changes' : 'Create Company'}
          </Button>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        title={editing ? 'Save Changes' : 'Create Company'}
        message={editing ? 'Are you sure you want to save these changes?' : 'Are you sure you want to create this company?'}
        confirmLabel={editing ? 'Save' : 'Create'}
        variant="primary"
        loading={saving}
        onConfirm={performSave}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
